import { CartApi } from '@/services/cart/api/CartApi';
import { Cart, CartItem, DeletedCartItem } from '@/services/cart/model/cart';
import {
    FairCartApi, FairCartDeletedItemDto, FairCartDto, FairCartItemDto, FairCartSubmitDto,
    OrderDto
} from '@eurogast/hub-client-typescript';
import { ApiService } from '@/services/ApiService';
import Nara from '@/domain/product/Nara';

// noinspection JSMethodCanBeStatic
export class FairCartApiWrapper implements CartApi {

    private fairCode: string;
    private api: FairCartApi;

    constructor(fairCode: string) {
        this.fairCode = fairCode;
        this.api = ApiService.wrap(FairCartApi);
    }

    public async addItemToOwnShoppingCart(
        tenant: string,
        customerId: string,
        item: CartItem
    ): Promise<Cart> {
        const apiItem = this.toApi(item);

        const apiResponse = await this.api.addItemToFairCart(
            tenant,
            customerId,
            this.fairCode,
            apiItem
        );

        return this.toStoreCart(apiResponse);
    }

    public async addItemToOwnShoppingCartNoDetail(
        tenant: string,
        customerId: string,
        item: CartItem
    ): Promise<CartItem> {
        const apiItem = this.toApi(item);

        const apiResponse = await this.api.addItemToFairCartNoDetail(
            tenant,
            customerId,
            this.fairCode,
            apiItem
        );

        return this.toStoreCartItem(apiResponse);
    }

    public async clearOwnShoppingCart(
        tenant: string,
        customerId: string
    ): Promise<Cart> {
        const apiResponse = await this.api.clearFairCart(
            tenant,
            customerId,
            this.fairCode
        );

        return this.toStoreCart(apiResponse);
    }

    public async getOwnShoppingCart(
        tenant: string,
        customerId: string
    ): Promise<Cart> {
        const apiResponse = await this.api.getFairCart(
            tenant,
            customerId,
            this.fairCode
        );

        return this.toStoreCart(apiResponse);
    }

    public async removeItemFromOwnShoppingCart(
        tenant: string,
        customerId: string,
        item: CartItem
    ): Promise<Cart> {
        const apiItem = this.toApi(item);

        const apiResponse = await this.api.removeItemFromFairCart(
            tenant,
            customerId,
            this.fairCode,
            apiItem
        );

        return this.toStoreCart(apiResponse);
    }

    public async submitOwnShoppingCart(
      tenant: string,
      customerId: string,
      orderInformation: FairCartSubmitDto
    ): Promise<OrderDto> {
        return this.api.submitFairCart(
          tenant,
          customerId,
          this.fairCode,
          orderInformation
        );
    }

    private toStoreCart(fairCartDto: FairCartDto): Cart {
        return new Cart(
            fairCartDto.customerId,
            fairCartDto.userId,
            fairCartDto.items.map((el) => this.toStoreCartItem(el)),
            null,
            fairCartDto.deletedItems.map((el) => this.toStoreDeletedItem(el))
        );
    }

    private toStoreCartItem(fairCartItem: FairCartItemDto): CartItem {
        const item = new CartItem(
            fairCartItem.sku,
            fairCartItem.amount,
            fairCartItem.unitAbbreviation,
            fairCartItem.unit,
            fairCartItem.product,
            fairCartItem.lastUpdatedAt
        );

        item.naras = Nara.findAvailableNaras(fairCartItem.naraDto.naraCodes);
        return item;
    }

    private toStoreDeletedItem(fairCartItem: FairCartDeletedItemDto): DeletedCartItem {
        return new DeletedCartItem(
            fairCartItem.sku,
            fairCartItem.unitAbbr,
            fairCartItem.deletedAt
        );
    }

    private toApi(cartItem: CartItem): FairCartItemDto {
        return {
            sku: cartItem.sku,
            amount: cartItem.amount,
            unitAbbreviation: cartItem.unitAbbreviation,
            unit: cartItem.unit,
            product: cartItem.product,
            lastUpdatedAt: cartItem.lastUpdatedAt
        };
    }

}
